import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Home2025Quotes } from '../../types/contentstack';
import { mediaScreen } from '../../utils/mediaScreen';
import { Col, ResponsiveContainer, Row } from '../responsive';
import { Icon } from '../ui/icon';
import Link from 'next/link';

/**
 * Quotes carousel component
 * - Auto advances every SLIDE_MS with animated progress bar for active slide
 * - Manual navigation via previous / next arrows (hidden on mobile)
 * - Accessible: buttons have labels; list announced but animation not disruptive
 */
export const Quotes: React.FC<Home2025Quotes> = ({ eyebrow, quotes }) => {
  const items = (quotes || []).filter(Boolean) as NonNullable<
    Home2025Quotes['quotes']
  >;

  const [index, setIndex] = React.useState(0);
  const SLIDE_MS = 20000; // duration for each quote
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (!items.length) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % items.length);
    }, SLIDE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [index, items.length]);

  if (!items.length) return null;

  const current = items[index];

  function go(step: number) {
    setIndex((i) => {
      const next = (i + step + items.length) % items.length;
      return next;
    });
  }

  return (
    <Section aria-label="Customer quotes">
      {/* <img className="quotes-angle" src="/img/quotes-angle.svg" alt="" /> */}

      <Inner>
        <Row>
          <Col xs={12} md={4} style={{ position: 'relative' }}>
            <img className="image" src="/img/quotes-image.svg" alt="" />
          </Col>

          <Col xs={12} md={8}>
            <Eyebrow>{eyebrow}</Eyebrow>

            <QuoteWrapper>
              <QuoteText
                dangerouslySetInnerHTML={{ __html: current?.text || '' }}
              />

              <div className="quote-meta">
                <Author
                  dangerouslySetInnerHTML={{ __html: current?.author || '' }}
                />

                {!!current?.link?.href && (
                  <CaseStudyLink>
                    <Link href={current.link.href} legacyBehavior passHref>
                      <a className="case-link">
                        {current.link.title}
                        <Icon name="arrow-right" size={16} />
                      </a>
                    </Link>
                  </CaseStudyLink>
                )}
              </div>
            </QuoteWrapper>

            <Controls>
              <Arrows className="desktop-only" aria-hidden="true">
                <ArrowButton
                  type="button"
                  onClick={() => go(-1)}
                  aria-label="Previous quote"
                  $variant="outline"
                >
                  <Icon name="arrow-left" size={18} />
                </ArrowButton>
                <ArrowButton
                  type="button"
                  onClick={() => go(1)}
                  aria-label="Next quote"
                  $variant="outline"
                >
                  <Icon name="arrow-right" size={18} />
                </ArrowButton>
              </Arrows>

              <Indicators role="tablist" aria-label="Quotes progress">
                {items.map((_, i) => (
                  <Indicator
                    key={i}
                    role="tab"
                    aria-selected={i === index}
                    tabIndex={i === index ? 0 : -1}
                    onClick={() => setIndex(i)}
                  >
                    {i === index && <IndicatorFill duration={SLIDE_MS} />}
                  </Indicator>
                ))}
              </Indicators>
            </Controls>
          </Col>
        </Row>
      </Inner>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  padding: 100px 0;
  margin-bottom: 80px;
  overflow: hidden;
  background: white;

  .quotes-angle {
    position: absolute;
    bottom: 0;
    left: 0;
  }

  ${mediaScreen('xs', 'md')} {
    padding: 60px 0 70px;

    .quotes-angle {
      top: 0;
    }
  }
`;

const Inner = styled(ResponsiveContainer)`
  position: relative;

  .image {
    max-width: unset;
    position: relative;
    width: 120%;
    top: -5%;

    ${mediaScreen('md', 'xl')} {
      top: 5%;
      width: 140%;
    }
    ${mediaScreen('sm', 'lg')} {
      width: 130%;
      top: 10%;
      left: -20%;
    }
    ${mediaScreen('xs', 'md')} {
      width: 70%;
      opacity: 0.8;
      margin-bottom: -30px;
    }
  }
`;

const Eyebrow = styled.h5`
  letter-spacing: 4px;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.colors.eb1};
  margin: 0 0 40px;
  text-transform: uppercase;
  position: relative;
  z-index: 2;

  ${mediaScreen('xs', 'md')} {
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const QuoteWrapper = styled.div`
  max-width: 880px;
  position: relative;
  z-index: 2;
  color: ${(p) => p.theme.colors.white};

  ${mediaScreen('sm')} {
    .quote-meta {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-bottom: 60px;

      > * {
        margin: 0;
      }
    }
  }
  ${mediaScreen('xs', 'md')} {
    max-width: 100%;
  }
`;

const QuoteText = styled.p`
  font-size: 36px;
  line-height: 1.48;
  font-weight: 400;
  margin: 0 0 40px;
  color: ${(p) => p.theme.colors.slate1};
  ${mediaScreen('xs', 'md')} {
    font-size: 24px;
    line-height: 1.45;
    margin-bottom: 32px;
  }
`;

const Author = styled.p`
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 28px;
  color: ${(p) => p.theme.colors.eb1};
  font-weight: 400;
  max-width: 370px;
  ${mediaScreen('xs', 'md')} {
    margin-bottom: 44px;
  }
`;

const CaseStudyLink = styled.div`
  margin: 0 0 56px;
  .case-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 16px;
    font-weight: 300;
    color: ${(p) => p.theme.colors.slate1};
    position: relative;
  }

  ${mediaScreen('xs', 'md')} {
    margin-bottom: 60px;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  position: relative;
  z-index: 2;
  ${mediaScreen('xs', 'md')} {
    gap: 16px;
    flex-direction: column-reverse;
    align-items: flex-start;
  }
`;

const Arrows = styled.div`
  display: flex;
  gap: 18px;
  ${mediaScreen('xs', 'md')} {
    display: none;
  }
`;

const ArrowButton = styled.button<{ $variant: 'outline' | 'filled' }>`
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: ${(p) =>
    p.$variant === 'filled' ? p.theme.colors.teal : 'transparent'};
  border: 1px solid
    ${(p) =>
      p.$variant === 'filled' ? p.theme.colors.teal : p.theme.colors.slate3};
  color: ${(p) =>
    p.$variant === 'filled' ? '#0d1117' : p.theme.colors.slate3};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;

const Indicators = styled.div`
  display: flex;
  gap: 56px;
  ${mediaScreen('xs', 'md')} {
    gap: 32px;
    width: 100%;
  }
`;

const Indicator = styled.button`
  position: relative;
  width: 160px;
  height: 2px;
  background: ${(p) => p.theme.colors.eb3};
  border: none;
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  border-radius: 2px;
  appearance: none;
  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.teal};
    outline-offset: 3px;
  }
  ${mediaScreen('xs', 'md')} {
    flex: 1;
    width: auto;
  }
`;

const fill = keyframes`
	from { width: 0; }
	to { width: 100%; }
`;

const IndicatorFill = styled.span<{ duration: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: ${(p) => p.theme.colors.teal};
  animation: ${fill} ${(p) => p.duration}ms ease forwards;
`;
